import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ProveedorService } from '../services/proveedor.service';
import { CorteProveedorService } from '../services/corteProveedor.service';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { CortesDialogComponent } from '../cortes-lista/cortes-dialog/cortes-dialog.component';
import moment from 'moment';
import 'moment/locale/es';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { AuthService } from '../services/auth.service';
import { take } from 'rxjs/operators';

interface ProductoVendido {
  id: number;
  detalleVentaId: number;
  productoId: number;
  productoNombre: string;
  productoCodigo: string;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
  fechaVenta: string;
  ventaId: number;
  pagado: boolean;
}

interface Corte {
  id: number;
  proveedorId: number;
  proveedorNombre?: string;
  proveedor?: {
    id: number;
    nombre: string;
    descripcion?: string;
  };
  montoTotal: number;
  montoPagado: number;
  estado: string;
  observaciones?: string;
  createdAt: string;
  detalles: any[];
}

@Component({
  selector: 'app-cortes-proveedor',
  templateUrl: './cortes-proveedor.component.html',
  styleUrls: ['./cortes-proveedor.component.scss']
})
export class CortesProveedorComponent implements OnInit {
  filterForm: FormGroup;
  loadingProviders = false;
  loadingProductos = false;
  loadingCortes = false;
  providers: any[] = [];
  productosVendidos: ProductoVendido[] = [];
  productosFiltrados: ProductoVendido[] = [];
  cortes: Corte[] = [];
  corteSeleccionado: Corte | null = null;

  productosSeleccionados: number[] = [];
  mostrarSoloPendientes = true;
  
  // Filtros de búsqueda
  nombreFiltro: string = '';
  etiquetaFiltro: string = '';

  periodTitle = '';
  periodSubtitle = '';
  private customDateChangeTimeout: any = null;

  @ViewChild('dayPicker') dayPicker!: MatDatepicker<Date>;

  // Formulario para crear corte
  corteForm: FormGroup;
  mostrarFormularioCorte = false;

  // Formulario para pagar corte
  pagoForm: FormGroup;
  mostrarFormularioPago = false;
  productosSeleccionadosPago: number[] = [];

  // Control de acceso
  isAdministrador = false;

  constructor(
    private fb: FormBuilder,
    private proveedorService: ProveedorService,
    private corteProveedorService: CorteProveedorService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private authService: AuthService
  ) {
    moment.locale('es');
    this.filterForm = this.fb.group({
      proveedorId: [null],
      viewType: ['today'],
      startDate: [null],
      endDate: [null],
      soloPendientes: [true],
      nombre: [''],
      etiqueta: ['']
    });

    this.corteForm = this.fb.group({
      observaciones: ['']
    });

    this.pagoForm = this.fb.group({
      monto: [0],
      observaciones: ['']
    });
  }

  ngOnInit(): void {
    this.checkUserRole();
    this.loadProviders();
    this.setDefaultDateRange();
    this.updatePeriodDisplay();
    this.setupFormSubscriptions();
  }

  private checkUserRole(): void {
    // Verificar primero si el usuario ya está disponible en el snapshot
    const currentUser = this.authService.getCurrentUserSnapshot();
    
    if (currentUser?.['rol']) {
      // Verificar si el rol es "Administrador"
      const rolNombre = typeof currentUser['rol'] === 'string' 
        ? currentUser['rol'] 
        : currentUser['rol']?.['rol'];
      
      this.isAdministrador = rolNombre === 'Administrador';
    } else {
      // Si no hay usuario en el snapshot, intentar obtenerlo del observable
      this.authService.currentUser$.pipe(take(1)).subscribe(user => {
        if (user?.['rol']) {
          const rolNombre = typeof user['rol'] === 'string' 
            ? user['rol'] 
            : user['rol']?.['rol'];
          
          this.isAdministrador = rolNombre === 'Administrador';
        }
      });
    }
  }

  private setupFormSubscriptions(): void {
    this.filterForm.get('proveedorId')?.valueChanges.subscribe(value => {
      if (value) {
        this.cargarProductosVendidos();
        this.cargarCortes();
      } else {
        this.productosVendidos = [];
        this.productosFiltrados = [];
        this.cortes = [];
        this.productosSeleccionados = [];
      }
    });

    this.filterForm.get('viewType')?.valueChanges.subscribe((viewType) => {
      this.setDefaultDateRange();
      if (viewType !== 'custom') {
        this.cargarProductosVendidos();
        this.cargarCortes();
      } else {
        this.updatePeriodDisplay();
      }
    });

    this.filterForm.get('startDate')?.valueChanges.subscribe(() => {
      if (this.filterForm.get('viewType')?.value === 'custom') {
        this.handleCustomDateChange();
      }
    });

    this.filterForm.get('endDate')?.valueChanges.subscribe(() => {
      if (this.filterForm.get('viewType')?.value === 'custom') {
        this.handleCustomDateChange();
      }
    });

    this.filterForm.get('soloPendientes')?.valueChanges.subscribe(() => {
      this.cargarProductosVendidos();
    });
  }

  private handleCustomDateChange(): void {
    if (this.customDateChangeTimeout) {
      clearTimeout(this.customDateChangeTimeout);
    }

    this.customDateChangeTimeout = setTimeout(() => {
      const start = this.filterForm.get('startDate')?.value;
      const end = this.filterForm.get('endDate')?.value;
      
      if (start && end) {
        this.cargarProductosVendidos();
        this.cargarCortes();
      } else {
        this.updatePeriodDisplay();
      }
    }, 500);
  }

  private loadProviders(): void {
    this.loadingProviders = true;
    this.proveedorService.findAll().subscribe({
      next: (providers) => {
        this.providers = providers || [];
        this.loadingProviders = false;
      },
      error: () => {
        this.loadingProviders = false;
        this.toastr.error('No se pudieron cargar los proveedores');
      }
    });
  }

  private setDefaultDateRange(): void {
    const viewType = this.filterForm.get('viewType')?.value;
    let start: moment.Moment | null = null;
    let end: moment.Moment | null = null;

    switch (viewType) {
      case 'week':
        start = moment().startOf('isoWeek');
        end = moment().endOf('isoWeek');
        break;
      case 'month':
        start = moment().startOf('month');
        end = moment().endOf('month');
        break;
      case 'custom':
        const currentStart = this.filterForm.get('startDate')?.value;
        const currentEnd = this.filterForm.get('endDate')?.value;
        if (currentStart && currentEnd) {
          start = moment(currentStart);
          end = moment(currentEnd);
        } else {
          return;
        }
        break;
      case 'today':
      default:
        start = moment().startOf('day');
        end = moment().endOf('day');
        break;
    }

    if (start && end) {
      this.filterForm.patchValue({
        startDate: start.toDate(),
        endDate: end.toDate()
      }, { emitEvent: false });
    }
    this.updatePeriodDisplay();
  }

  changePeriod(direction: 'prev' | 'next'): void {
    const viewType = this.filterForm.get('viewType')?.value ?? 'today';
    let start = this.filterForm.get('startDate')?.value ? moment(this.filterForm.get('startDate')?.value) : moment();
    let end = this.filterForm.get('endDate')?.value ? moment(this.filterForm.get('endDate')?.value) : moment();

    const step = direction === 'next' ? 1 : -1;

    switch (viewType) {
      case 'today':
        start = start.add(step, 'day').startOf('day');
        end = start.clone().endOf('day');
        break;
      case 'week':
        start = start.add(step, 'week').startOf('isoWeek');
        end = start.clone().endOf('isoWeek');
        break;
      case 'month':
        start = start.add(step, 'month').startOf('month');
        end = start.clone().endOf('month');
        break;
      case 'custom':
        const currentStart = this.filterForm.get('startDate')?.value ? moment(this.filterForm.get('startDate')?.value).startOf('day') : moment().startOf('day');
        const currentEnd = this.filterForm.get('endDate')?.value ? moment(this.filterForm.get('endDate')?.value).endOf('day') : moment().endOf('day');
        const diffDays = currentEnd.diff(currentStart, 'days');
        start = currentStart.add(step * (diffDays + 1), 'days');
        end = start.clone().add(diffDays, 'days').endOf('day');
        break;
      default:
        start = start.add(step, 'day').startOf('day');
        end = start.clone().endOf('day');
        break;
    }

    this.filterForm.patchValue(
      {
        startDate: start.toDate(),
        endDate: end.toDate()
      },
      { emitEvent: false }
    );

    this.updatePeriodDisplay();
    this.cargarProductosVendidos();
    this.cargarCortes();
  }

  private updatePeriodDisplay(): void {
    const viewType = this.filterForm.get('viewType')?.value ?? 'today';
    const startValue = this.filterForm.get('startDate')?.value;
    const endValue = this.filterForm.get('endDate')?.value;

    if (!startValue || !endValue) {
      this.periodTitle = this.getViewTitle(viewType);
      this.periodSubtitle = 'Selecciona un rango de fechas para ver los productos.';
      return;
    }

    const start = moment(startValue);
    const end = moment(endValue);

    this.periodTitle = this.getViewTitle(viewType);
    this.periodSubtitle = this.buildPeriodSubtitle(viewType, start, end);
  }

  private getViewTitle(viewType: string): string {
    switch (viewType) {
      case 'today':
        return 'Vista del día';
      case 'week':
        return 'Semana seleccionada';
      case 'month':
        return 'Mes seleccionado';
      case 'custom':
        return 'Rango personalizado';
      default:
        return 'Periodo seleccionado';
    }
  }

  private buildPeriodSubtitle(viewType: string, start: moment.Moment, end: moment.Moment): string {
    const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
    const formatDay = (date: moment.Moment) => capitalize(date.format('dddd D [de] MMMM YYYY'));

    if (viewType === 'today' || start.isSame(end, 'day')) {
      return formatDay(start);
    }

    if (viewType === 'week') {
      return `Del ${formatDay(start)} al ${formatDay(end)}`;
    }

    if (viewType === 'month') {
      return capitalize(start.format('MMMM YYYY'));
    }

    return `Del ${formatDay(start)} al ${formatDay(end)}`;
  }

  cargarProductosVendidos(): void {
    const proveedorId = this.filterForm.get('proveedorId')?.value;
    if (!proveedorId) {
      this.productosVendidos = [];
      this.productosFiltrados = [];
      return;
    }

    this.loadingProductos = true;
    const { startDate, endDate, soloPendientes, nombre, etiqueta } = this.filterForm.value;

    if (!startDate || !endDate) {
      this.loadingProductos = false;
      return;
    }

    // Construir filtros para el nuevo endpoint POST
    const filtros: any = {
      proveedorId: proveedorId,
      fechaInicio: moment(startDate).startOf('day').toISOString(),
      fechaFin: moment(endDate).endOf('day').toISOString(),
      soloPendientes: soloPendientes !== false ? 1 : 0
    };

    // Agregar filtros opcionales si tienen valor
    if (nombre && nombre.trim()) {
      filtros.nombre = nombre.trim();
    }
    if (etiqueta && etiqueta.trim()) {
      filtros.etiqueta = etiqueta.trim();
    }

    this.corteProveedorService.buscarProductosVendidos(filtros).subscribe({
      next: (productos) => {
        // Normalizar los datos para asegurar que tengan la estructura correcta
        this.productosVendidos = (productos || []).map((p: any) => ({
          ...p,
          productoNombre: p.producto?.nombre || p.productoNombre || p.nombre || 'Sin nombre',
          productoCodigo: p.producto?.codigo || p.productoCodigo || p.codigo || '',
          fechaVenta: p.venta?.fecha || p.fechaVenta || p.fecha || p.createdAt || new Date().toISOString(),
          detalleVentaId: p.id || p.detalleVentaId,
          cantidad: p.cantidad || 0,
          precioUnitario: p.precioUnitario || 0,
          subtotal: p.subtotal || 0,
          pagado: p.pagadoAProveedor || false
        }));
        this.productosFiltrados = [...this.productosVendidos];
        this.loadingProductos = false;
      },
      error: () => {
        this.toastr.error('Error al cargar los productos vendidos');
        this.loadingProductos = false;
      }
    });
  }

  cargarCortes(): void {
    const proveedorId = this.filterForm.get('proveedorId')?.value;
    if (!proveedorId) {
      this.cortes = [];
      return;
    }

    this.loadingCortes = true;
    const { startDate, endDate } = this.filterForm.value;

    // Construir filtros para el nuevo endpoint POST
    const filtros: any = {
      proveedorId: proveedorId
    };

    // Agregar rango de fechas si están disponibles
    if (startDate && endDate) {
      filtros.fechaInicio = moment(startDate).startOf('day').toISOString();
      filtros.fechaFin = moment(endDate).endOf('day').toISOString();
    }

    this.corteProveedorService.buscarCortes(filtros).subscribe({
      next: (response) => {
        // Manejar respuesta paginada o array directo
        if (Array.isArray(response)) {
          this.cortes = response;
        } else if (response.data) {
          this.cortes = response.data;
        } else {
          this.cortes = [];
        }
        this.loadingCortes = false;
      },
      error: (error) => {
        const errorMessage = error?.error?.message || 'Error al cargar los cortes';
        this.toastr.error(errorMessage);
        this.loadingCortes = false;
        this.cortes = [];
      }
    });
  }

  seleccionarProducto(detalleVentaId: number): void {
    const index = this.productosSeleccionados.indexOf(detalleVentaId);
    if (index > -1) {
      this.productosSeleccionados.splice(index, 1);
    } else {
      this.productosSeleccionados.push(detalleVentaId);
    }
  }

  toggleSeleccionarTodos(checked: boolean): void {
    if (checked) {
      this.productosSeleccionados = this.productosFiltrados.map(p => p.detalleVentaId);
    } else {
      this.productosSeleccionados = [];
    }
  }

  estaSeleccionado(detalleVentaId: number): boolean {
    return this.productosSeleccionados.includes(detalleVentaId);
  }

  calcularTotalSeleccionado(): number {
    return this.productosVendidos
      .filter(p => this.productosSeleccionados.includes(p.detalleVentaId))
      .reduce((sum, p) => sum + Number(p.subtotal || 0), 0);
  }

  toggleFormularioCorte(): void {
    this.mostrarFormularioCorte = !this.mostrarFormularioCorte;
    if (!this.mostrarFormularioCorte) {
      this.corteForm.reset();
    }
  }

  crearCorte(): void {
    const proveedorId = this.filterForm.get('proveedorId')?.value;
    if (!proveedorId) {
      this.toastr.warning('Selecciona un proveedor');
      return;
    }

    if (this.productosSeleccionados.length === 0) {
      this.toastr.warning('Selecciona al menos un producto para crear el corte');
      return;
    }

    const corte = {
      proveedorId: proveedorId,
      detalleVentaIds: this.productosSeleccionados,
      observaciones: this.corteForm.get('observaciones')?.value || ''
    };

    this.corteProveedorService.crearCorteConProductos(corte).subscribe({
      next: (corteCreado) => {
        this.toastr.success('Corte creado exitosamente');
        this.productosSeleccionados = [];
        this.mostrarFormularioCorte = false;
        this.corteForm.reset();
        this.cargarProductosVendidos();
        this.cargarCortes();
      },
      error: () => {
        this.toastr.error('Error al crear el corte');
      }
    });
  }

  verCorte(corte: Corte): void {
    this.dialog.open(CortesDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: { corte: corte }
    }).afterClosed().subscribe((result) => {
      // Recargar cortes si se hizo algún cambio
      if (result) {
        this.cargarCortes();
        this.cargarProductosVendidos();
      }
    });
  }

  toggleFormularioPago(corte: Corte): void {
    this.corteSeleccionado = corte;
    this.mostrarFormularioPago = !this.mostrarFormularioPago;
    if (this.mostrarFormularioPago) {
      const saldoPendiente = Number(corte.montoTotal || 0) - Number(corte.montoPagado || 0);
      this.pagoForm.patchValue({ monto: saldoPendiente });
      this.productosSeleccionadosPago = [];
    } else {
      this.pagoForm.reset();
      this.productosSeleccionadosPago = [];
    }
  }

  seleccionarProductoPago(detalleCorteId: number): void {
    const index = this.productosSeleccionadosPago.indexOf(detalleCorteId);
    if (index > -1) {
      this.productosSeleccionadosPago.splice(index, 1);
    } else {
      this.productosSeleccionadosPago.push(detalleCorteId);
    }
  }

  estaSeleccionadoPago(detalleCorteId: number): boolean {
    return this.productosSeleccionadosPago.includes(detalleCorteId);
  }

  pagarCorte(): void {
    if (!this.corteSeleccionado) return;

    const monto = Number(this.pagoForm.get('monto')?.value || 0);
    if (monto <= 0) {
      this.toastr.warning('El monto debe ser mayor a cero');
      return;
    }

    const pago = {
      monto: monto,
      observaciones: this.pagoForm.get('observaciones')?.value || '',
      detalleCorteIds: this.productosSeleccionadosPago.length > 0 ? this.productosSeleccionadosPago : undefined
    };

    const esPagoParcial = this.productosSeleccionadosPago.length > 0;
    const servicio = esPagoParcial
      ? this.corteProveedorService.pagarProductosEspecificos(this.corteSeleccionado.id, pago)
      : this.corteProveedorService.pagarCorteCompleto(this.corteSeleccionado.id, pago);

    servicio.subscribe({
      next: () => {
        this.toastr.success('Pago registrado exitosamente');
        this.mostrarFormularioPago = false;
        this.pagoForm.reset();
        this.productosSeleccionadosPago = [];
        this.cargarCortes();
        this.cargarProductosVendidos();
      },
      error: () => {
        this.toastr.error('Error al registrar el pago');
      }
    });
  }

  getSaldoPendiente(corte: Corte): number {
    return Number(corte.montoTotal || 0) - Number(corte.montoPagado || 0);
  }

  getEstadoCorte(corte: Corte): string {
    const saldo = this.getSaldoPendiente(corte);
    if (saldo <= 0) return 'Pagado';
    if (Number(corte.montoPagado || 0) > 0) return 'Parcial';
    return 'Pendiente';
  }

  openDayPicker(): void {
    const viewType = this.filterForm.get('viewType')?.value;
    if (viewType === 'today' && this.dayPicker) {
      this.dayPicker.open();
    }
  }

  onDaySelected(event: MatDatepickerInputEvent<Date>): void {
    const date = event.value;
    if (date) {
      const selectedDate = moment(date);
      this.filterForm.patchValue({
        startDate: selectedDate.startOf('day').toDate(),
        endDate: selectedDate.endOf('day').toDate()
      }, { emitEvent: false });
      this.updatePeriodDisplay();
      this.cargarProductosVendidos();
      this.cargarCortes();
    }
  }

  get isDayView(): boolean {
    return this.filterForm.get('viewType')?.value === 'today';
  }
}

